import { apiErrorField, reachedServer } from '@agos/api-client'
import type { CustomerUser } from '@agos/customer-core'
import { tt } from '@agos/shared'

/**
 * What the three auth forms check before they send anything.
 *
 * **The rules are the server's**, and where legacy's differ they are replaced rather than kept. The
 * phone is the case that matters: legacy asks only that it is non-empty —
 *
 * ```ts
 * if (!form.phone.trim()) errors.phone = tt("Telefónne číslo je povinné");
 * ```
 *
 * — while `RegisterRequest` is `atLeast(phone, 6, "Zadajte telefónne číslo")`. A three-digit number
 * passes the form, is refused by the server, and the customer reads the failure as the site being
 * broken. Applying the server's rule in the field is the same rule, one round trip earlier.
 *
 * The e-mail pattern is legacy's own, unchanged. It is loose, and it is meant to be: the server
 * normalises and the mail either arrives or it does not. A stricter client-side pattern rejects
 * addresses that work.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/** The server's own minimum, from `atLeast(password, 6, "Heslo musí mať aspoň 6 znakov")`. */
export const MIN_PASSWORD = 6
/** `atLeast(phone, 6, "Zadajte telefónne číslo")`. */
export const MIN_PHONE = 6

export interface RegistrationForm {
  firstName: string
  lastName: string
  phone: string
  email: string
  password: string
  confirmPassword: string
}

/** Field name → message. Empty means the form may be sent. */
export function registrationProblems(form: RegistrationForm): Record<string, string> {
  const problems: Record<string, string> = {}
  if (form.firstName.trim() === '') problems.firstName = tt('Meno je povinné')
  if (form.lastName.trim() === '') problems.lastName = tt('Priezvisko je povinné')
  if (form.phone.trim().length < MIN_PHONE) problems.phone = tt('Zadajte telefónne číslo')
  if (!EMAIL.test(form.email.trim())) problems.email = tt('Zadajte platný e-mail')
  if (form.password.length < MIN_PASSWORD) problems.password = tt('Heslo musí mať aspoň 6 znakov')
  // Checked as the second field is typed rather than only on submit, which is where a mismatch is
  // noticed and costs nothing to find.
  if (form.confirmPassword !== '' && form.confirmPassword !== form.password) {
    problems.confirmPassword = tt('Heslá sa nezhodujú')
  }
  return problems
}

/** Whether the register button does anything. The confirmation must be present, not merely matching. */
export function mayRegister(form: RegistrationForm): boolean {
  return (
    Object.keys(registrationProblems(form)).length === 0 &&
    form.confirmPassword === form.password &&
    form.confirmPassword !== ''
  )
}

export function maySignIn(email: string, password: string): boolean {
  return email.trim() !== '' && password !== ''
}

/** Normalised before sending. The server does it too; agreeing costs nothing and avoids a surprise. */
export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase()
}

export function newPasswordProblem(password: string, confirmation: string): string {
  if (password.length < MIN_PASSWORD) return tt('Heslo musí mať aspoň 6 znakov')
  if (confirmation !== '' && confirmation !== password) return tt('Heslá sa nezhodujú')
  return ''
}

export function mayResetPassword(password: string, confirmation: string): boolean {
  return (
    newPasswordProblem(password, confirmation) === '' &&
    confirmation === password &&
    confirmation !== ''
  )
}

/**
 * A 2xx that is not actually a sign-in.
 *
 * All four endpoints answer `{ user, token }`, and every one of legacy's pages treats a missing `user`
 * **or** a missing `token` as a failure even on a 200. That is right — a half-answer that is accepted
 * leaves the app looking signed in and unable to make a single authorised call — and it is kept.
 */
export function sessionFrom(
  payload: unknown,
): { user: Record<string, unknown>; token: string } | null {
  if (typeof payload !== 'object' || payload === null) return null
  const row = payload as Record<string, unknown>
  const user = row.user
  const token = row.token
  if (typeof user !== 'object' || user === null) return null
  if (typeof token !== 'string' || token.trim() === '') return null
  return { user: user as Record<string, unknown>, token }
}

/**
 * The signed-in customer, field by field - §10A.120.
 *
 * **This replaces `session.user as unknown as CustomerUser` at all three sign-in call sites, and the
 * cast is why the owner could not see their discount.** `sessionFrom` above deliberately answers
 * `Record<string, unknown>`: it narrows *"is this a sign-in at all"* and cannot know the user's
 * shape. So the call sites cast - and `CustomerUser` declares eight fields while the login response
 * carried five, which a plain assignment would have refused and `as unknown as` compiled happily.
 * `personalDiscountType`, `personalDiscountValue` and `personalDiscountStackable` arrived
 * `undefined`, `personalDiscountCents` returned 0, and the *trvalá zľava* line never rendered while
 * the server took 30 % off for real.
 *
 * Reading each field by name is what makes that a test failure instead of a silence: a field added to
 * the wire and forgotten here is a field this function provably does not carry.
 *
 * <p>`id` is the only one that can refuse the whole object. A session without one cannot make an
 * authorised call, and legacy treats that as a failed sign-in too.
 */
export function customerUserFrom(user: Record<string, unknown>): CustomerUser | null {
  const id = user.id
  if (typeof id !== 'number') return null
  const discountType = user.personalDiscountType
  return {
    id,
    email: typeof user.email === 'string' ? user.email : null,
    name: typeof user.name === 'string' ? user.name : null,
    phone: typeof user.phone === 'string' ? user.phone : null,
    pointsBalance: typeof user.pointsBalance === 'number' ? user.pointsBalance : 0,
    loyaltyLevel: typeof user.loyaltyLevel === 'string' ? user.loyaltyLevel : undefined,
    // `percent` and `fixed` are the two the column holds; anything else is not a discount, and
    // `personalDiscountCents` keys off this being non-null.
    personalDiscountType:
      discountType === 'percent' || discountType === 'fixed' ? discountType : null,
    personalDiscountValue:
      typeof user.personalDiscountValue === 'number' ? user.personalDiscountValue : 0,
    // Not stackable unless the server says so. Legacy's default: with a permanent discount active
    // its server "ignoruje kupóny, odporúčania, Happy Hours aj Black Friday".
    personalDiscountStackable: user.personalDiscountStackable === true,
  }
}

/**
 * The message to show for a failed attempt.
 *
 * Three distinct cases, kept from legacy because a customer can act on the difference: the server's own
 * wording when there is one, a malformed answer, and a connection that never arrived.
 */
export function failureNote(failure: unknown, fallback: string): string {
  const said = apiErrorField(failure, 'error')
  if (said !== null) return said
  // `ApiError` carries a response; anything without one never reached the server.
  if (!reachedServer(failure)) return tt('Nepodarilo sa spojiť so serverom')
  return fallback
}
